export type StateCode = "OR" | "NY";
export type FilingStatus = "single" | "married_jointly" | "head_of_household";
export type IncomeSource = "w2" | "self_employed";

export interface IncomeSourceEntry {
  id: string;
  revenueSource: string;
  paymentType: "hourly" | "salary";
  paymentAmount: number;
  hoursWorkedWeekly: number;
}

export interface IncomeInfo {
  incomeSource: IncomeSource;
  incomeSources: IncomeSourceEntry[];
  // Legacy single-source fields (backwards compatibility for saved profiles/share links)
  paymentType?: "hourly" | "salary";
  paymentAmount?: number;
  hoursWorkedWeekly?: number;
  businessDeductions: number;
  state: StateCode;
  filingStatus: FilingStatus;
  numChildren: number;
}

export interface TaxProfile {
  id: string;
  name: string;
  incomeInfo: IncomeInfo;
  createdAt: number;
}

export const TAX_PROFILES_STORAGE_KEY = "tax_calculator_profiles";

export const loadTaxProfiles = (): TaxProfile[] => {
  try {
    const stored = localStorage.getItem(TAX_PROFILES_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as TaxProfile[]) : [];
  } catch {
    return [];
  }
};

export const saveTaxProfiles = (profiles: TaxProfile[]): void => {
  localStorage.setItem(TAX_PROFILES_STORAGE_KEY, JSON.stringify(profiles));
};

export const generateTaxProfileId = (): string => {
  return `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export interface TaxBreakdown {
  federal_income_tax: number;
  medicare_tax: number;
  social_security_tax: number;
  state_income_tax: number;
  self_employment_tax: number;
}

export interface IncomeDetails {
  grossIncome: number;
  businessDeductions: number;
  netBusinessIncome: number;
  selfEmploymentTaxDeduction: number;
  standardDeduction: number;
  taxableIncome: number;
  netIncome: number;
  totalTax: number;
  taxBreakdown: TaxBreakdown;
  childTaxCredit: number;
  federalMarginalRate: number;
  stateMarginalRate: number;
  effectiveTaxRate: number;
  isSelfEmployed: boolean;
}

export interface IncomeDetailsResult {
  weeklyIncome: IncomeDetails;
  monthlyIncome: IncomeDetails;
  yearlyIncome: IncomeDetails;
}

type TaxBracket = {
  rate: number;
  lowerBound: number;
  upperBound: number;
};

export const FEDERAL_TAX_BRACKETS_2025: Record<FilingStatus, TaxBracket[]> = {
  single: [
    { rate: 0.1, lowerBound: 0, upperBound: 11925 },
    { rate: 0.12, lowerBound: 11925, upperBound: 48475 },
    { rate: 0.22, lowerBound: 48475, upperBound: 103350 },
    { rate: 0.24, lowerBound: 103350, upperBound: 197300 },
    { rate: 0.32, lowerBound: 197300, upperBound: 250525 },
    { rate: 0.35, lowerBound: 250525, upperBound: 626350 },
    { rate: 0.37, lowerBound: 626350, upperBound: Infinity },
  ],
  married_jointly: [
    { rate: 0.1, lowerBound: 0, upperBound: 23850 },
    { rate: 0.12, lowerBound: 23850, upperBound: 96950 },
    { rate: 0.22, lowerBound: 96950, upperBound: 206700 },
    { rate: 0.24, lowerBound: 206700, upperBound: 394600 },
    { rate: 0.32, lowerBound: 394600, upperBound: 501050 },
    { rate: 0.35, lowerBound: 501050, upperBound: 751600 },
    { rate: 0.37, lowerBound: 751600, upperBound: Infinity },
  ],
  head_of_household: [
    { rate: 0.1, lowerBound: 0, upperBound: 17000 },
    { rate: 0.12, lowerBound: 17000, upperBound: 64850 },
    { rate: 0.22, lowerBound: 64850, upperBound: 103350 },
    { rate: 0.24, lowerBound: 103350, upperBound: 197300 },
    { rate: 0.32, lowerBound: 197300, upperBound: 250525 },
    { rate: 0.35, lowerBound: 250525, upperBound: 626350 },
    { rate: 0.37, lowerBound: 626350, upperBound: Infinity },
  ],
};

export const STANDARD_DEDUCTIONS_2025: Record<FilingStatus, number> = {
  // IRS IR-2025-103 notes the "Under OBBB" standard deduction amounts for TY 2025.
  single: 15750,
  married_jointly: 31500,
  head_of_household: 23625,
};

export const CHILD_TAX_CREDIT_2025 = {
  amountPerChild: 2200,
  phaseOutStart: {
    single: 200000,
    married_jointly: 400000,
    head_of_household: 200000,
  },
};

export const OREGON_TAX_BRACKETS_2025: TaxBracket[] = [
  { rate: 0.0475, lowerBound: 0, upperBound: 4300 },
  { rate: 0.0675, lowerBound: 4300, upperBound: 10800 },
  { rate: 0.0875, lowerBound: 10800, upperBound: 125000 },
  { rate: 0.099, lowerBound: 125000, upperBound: 1250000 },
  { rate: 0.109, lowerBound: 1250000, upperBound: Infinity },
];

export const NEW_YORK_TAX_BRACKETS_2025: TaxBracket[] = [
  { rate: 0.04, lowerBound: 0, upperBound: 8500 },
  { rate: 0.045, lowerBound: 8500, upperBound: 11700 },
  { rate: 0.0525, lowerBound: 11700, upperBound: 13900 },
  { rate: 0.0585, lowerBound: 13900, upperBound: 21400 },
  { rate: 0.0625, lowerBound: 21400, upperBound: 80650 },
  { rate: 0.0685, lowerBound: 80650, upperBound: 215400 },
  { rate: 0.0965, lowerBound: 215400, upperBound: 1077550 },
  { rate: 0.103, lowerBound: 1077550, upperBound: 5000000 },
  { rate: 0.109, lowerBound: 5000000, upperBound: 25000000 },
  { rate: 0.109, lowerBound: 25000000, upperBound: Infinity },
];

export const STATE_TAX_BRACKETS: Record<StateCode, TaxBracket[]> = {
  OR: OREGON_TAX_BRACKETS_2025,
  NY: NEW_YORK_TAX_BRACKETS_2025,
};

export const STATE_NAMES: Record<StateCode, string> = {
  OR: "Oregon",
  NY: "New York",
};

export const FILING_STATUS_LABELS: Record<FilingStatus, string> = {
  single: "Single",
  married_jointly: "Married Filing Jointly",
  head_of_household: "Head of Household",
};

const calculateProgressiveTax = (
  income: number,
  brackets: TaxBracket[]
): { tax: number; marginalRate: number } => {
  let totalTax = 0;
  let marginalRate = 0;

  for (const bracket of brackets) {
    if (income > bracket.lowerBound) {
      const taxableInBracket =
        Math.min(income, bracket.upperBound) - bracket.lowerBound;
      totalTax += taxableInBracket * bracket.rate;
      marginalRate = bracket.rate;
    } else {
      break;
    }
  }

  return { tax: totalTax, marginalRate };
};

const calculateChildTaxCredit = (
  numChildren: number,
  grossIncome: number,
  filingStatus: FilingStatus
): number => {
  if (numChildren <= 0) return 0;

  const maxCredit = numChildren * CHILD_TAX_CREDIT_2025.amountPerChild;
  const phaseOutStart = CHILD_TAX_CREDIT_2025.phaseOutStart[filingStatus];

  if (grossIncome <= phaseOutStart) {
    return maxCredit;
  }

  const excessIncome = grossIncome - phaseOutStart;
  const reduction = Math.ceil(excessIncome / 1000) * 50;
  return Math.max(0, maxCredit - reduction);
};

export function calculateIncomeDetails({
  incomeSource,
  incomeSources,
  paymentAmount,
  paymentType,
  hoursWorkedWeekly,
  businessDeductions,
  state,
  filingStatus,
  numChildren,
}: IncomeInfo): IncomeDetailsResult {
  const MEDICARE_TAX_RATE = 0.0145;
  const SOCIAL_SECURITY_TAX_RATE = 0.062;
  const SS_WAGE_BASE_2025 = 176100;
  const SE_MEDICARE_RATE = 0.029;
  const SE_SS_RATE = 0.124;
  const SE_TAX_BASE_MULTIPLIER = 0.9235;

  const isSelfEmployed = incomeSource === "self_employed";

  const safeIncomeSources: IncomeSourceEntry[] =
    Array.isArray(incomeSources) && incomeSources.length > 0
      ? incomeSources
      : [
          {
            id: "legacy_0",
            revenueSource: "",
            paymentType: paymentType ?? "salary",
            paymentAmount: paymentAmount ?? 0,
            hoursWorkedWeekly: hoursWorkedWeekly ?? 40,
          },
        ];

  const yearlyGrossIncome = safeIncomeSources.reduce((sum, source) => {
    const sourcePaymentAmount =
      typeof source.paymentAmount === "number" &&
      Number.isFinite(source.paymentAmount)
        ? source.paymentAmount
        : 0;
    const sourceHoursWorkedWeekly =
      typeof source.hoursWorkedWeekly === "number" &&
      Number.isFinite(source.hoursWorkedWeekly)
        ? source.hoursWorkedWeekly
        : 0;
    const annual =
      source.paymentType === "hourly"
        ? sourcePaymentAmount * sourceHoursWorkedWeekly * 52
        : sourcePaymentAmount;
    return sum + Math.max(0, annual);
  }, 0);

  const yearlyBusinessDeductions = isSelfEmployed ? businessDeductions || 0 : 0;
  const netBusinessIncome = isSelfEmployed
    ? Math.max(0, yearlyGrossIncome - yearlyBusinessDeductions)
    : yearlyGrossIncome;

  let self_employment_tax = 0;
  let selfEmploymentTaxDeduction = 0;
  let medicare_tax = 0;
  let social_security_tax = 0;

  if (isSelfEmployed) {
    const seBaseIncome = netBusinessIncome * SE_TAX_BASE_MULTIPLIER;
    const ssSETax = Math.min(seBaseIncome, SS_WAGE_BASE_2025) * SE_SS_RATE;
    const medicareSETax = seBaseIncome * SE_MEDICARE_RATE;

    self_employment_tax = ssSETax + medicareSETax;
    selfEmploymentTaxDeduction = self_employment_tax / 2;
    medicare_tax = medicareSETax;
    social_security_tax = ssSETax;
  } else {
    medicare_tax = yearlyGrossIncome * MEDICARE_TAX_RATE;
    social_security_tax =
      Math.min(yearlyGrossIncome, SS_WAGE_BASE_2025) * SOCIAL_SECURITY_TAX_RATE;
  }

  const standardDeduction = STANDARD_DEDUCTIONS_2025[filingStatus];
  const adjustedGrossIncome = isSelfEmployed
    ? netBusinessIncome - selfEmploymentTaxDeduction
    : yearlyGrossIncome;
  const taxableIncome = Math.max(0, adjustedGrossIncome - standardDeduction);

  const federalResult = calculateProgressiveTax(
    taxableIncome,
    FEDERAL_TAX_BRACKETS_2025[filingStatus]
  );
  const federal_income_tax = federalResult.tax;
  const federalMarginalRate = federalResult.marginalRate;

  const childTaxCredit = calculateChildTaxCredit(
    numChildren,
    adjustedGrossIncome,
    filingStatus
  );

  const stateResult = calculateProgressiveTax(
    taxableIncome,
    STATE_TAX_BRACKETS[state]
  );
  const state_income_tax = stateResult.tax;
  const stateMarginalRate = stateResult.marginalRate;

  let totalTax: number;
  if (isSelfEmployed) {
    totalTax =
      federal_income_tax +
      state_income_tax +
      self_employment_tax -
      childTaxCredit;
  } else {
    totalTax =
      federal_income_tax +
      medicare_tax +
      social_security_tax +
      state_income_tax -
      childTaxCredit;
  }

  const effectiveTaxRate =
    yearlyGrossIncome > 0 ? totalTax / yearlyGrossIncome : 0;
  const yearlyNetIncome =
    (isSelfEmployed ? netBusinessIncome : yearlyGrossIncome) - totalTax;

  const weeklyGrossIncome = yearlyGrossIncome / 52;
  const weeklyTax = totalTax / 52;
  const weeklyNetIncome = yearlyNetIncome / 52;

  const monthlyGrossIncome = yearlyGrossIncome / 12;
  const monthlyTax = totalTax / 12;
  const monthlyNetIncome = yearlyNetIncome / 12;

  const baseDetails = {
    businessDeductions: yearlyBusinessDeductions,
    netBusinessIncome,
    selfEmploymentTaxDeduction,
    standardDeduction,
    childTaxCredit,
    federalMarginalRate,
    stateMarginalRate,
    effectiveTaxRate,
    isSelfEmployed,
  };

  return {
    weeklyIncome: {
      grossIncome: weeklyGrossIncome,
      taxableIncome: taxableIncome / 52,
      netIncome: weeklyNetIncome,
      totalTax: weeklyTax,
      taxBreakdown: {
        federal_income_tax: federal_income_tax / 52,
        medicare_tax: medicare_tax / 52,
        social_security_tax: social_security_tax / 52,
        state_income_tax: state_income_tax / 52,
        self_employment_tax: self_employment_tax / 52,
      },
      ...baseDetails,
    },
    monthlyIncome: {
      grossIncome: monthlyGrossIncome,
      taxableIncome: taxableIncome / 12,
      netIncome: monthlyNetIncome,
      totalTax: monthlyTax,
      taxBreakdown: {
        federal_income_tax: federal_income_tax / 12,
        medicare_tax: medicare_tax / 12,
        social_security_tax: social_security_tax / 12,
        state_income_tax: state_income_tax / 12,
        self_employment_tax: self_employment_tax / 12,
      },
      ...baseDetails,
    },
    yearlyIncome: {
      grossIncome: yearlyGrossIncome,
      taxableIncome,
      netIncome: yearlyNetIncome,
      totalTax,
      taxBreakdown: {
        federal_income_tax,
        medicare_tax,
        social_security_tax,
        state_income_tax,
        self_employment_tax,
      },
      ...baseDetails,
    },
  };
}

export const getMonthlyTaxFromProfile = (profile: TaxProfile): number => {
  return calculateIncomeDetails(profile.incomeInfo).monthlyIncome.totalTax;
};
